import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { TrendingUp, TrendingDown, Minus, Search, Trophy, Sparkles } from "lucide-react";

function TrendIcon({ trend }) {
  if (trend === "up") return <TrendingUp size={14} className="text-[var(--success)]" />;
  if (trend === "down") return <TrendingDown size={14} className="text-[var(--danger)]" />;
  return <Minus size={14} className="text-[var(--text-muted)]" />;
}

export default function CustomerStats() {
  const [rows, setRows] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/customers/stats/overview").then((r) => setRows(r.data));
    api.get("/revenue/forecast").then((r) => setForecast(r.data));
  }, []);

  const filtered = rows.filter((r) => !q || (r.name || "").toLowerCase().includes(q.toLowerCase()));
  const totalGross = rows.reduce((s, r) => s + (r.total_gross || 0), 0);
  const top3 = rows.slice(0, 3);

  return (
    <div data-testid="page-customer-stats">
      <PageHeader
        eyebrow="Analyse"
        title="Top-Kunden"
        description="Wer bringt den Umsatz? Sortiert nach Gesamt-Brutto. Trend vergleicht die letzten 30 Tage mit den vorigen 60."
      />

      {forecast && (
        <div className="mb-6 rounded-2xl p-5 bg-gradient-to-r from-[#EB75B5] to-[#c94ea0] text-white flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
              <Sparkles size={22} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider opacity-90">Umsatzprognose · nächste 30 Tage</div>
              <div className="font-display text-3xl font-bold">
                {forecast.total_forecast_next_30d.toFixed(2).replace(".", ",")} €
              </div>
              <div className="text-xs opacity-90">
                Ist letzte 30 T: {forecast.total_actual_last_30d.toFixed(2).replace(".", ",")} € ·
                Delta: <b className={forecast.delta >= 0 ? "" : "text-red-100"}>
                  {forecast.delta >= 0 ? "+" : ""}{forecast.delta.toFixed(2).replace(".", ",")} €
                </b>
              </div>
            </div>
          </div>
          <div className="text-right text-xs opacity-90 max-w-xs">
            Extrapoliert aus dem 30d-Umsatz je Kunde · gewichtet mit Trend (Up +10 %, Flat ±0 %, Down −10 %).
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-3 mb-6">
        {top3.map((c, i) => (
          <div key={c.customer_id} data-testid={`top-customer-${i}`}
               className="bg-white border border-[var(--border)] rounded-2xl p-5">
            <div className="flex items-center gap-2 text-[var(--text-muted)] text-xs uppercase tracking-wider mb-1">
              <Trophy size={14} className={i === 0 ? "text-[var(--pop-yellow-dark)]" : i === 1 ? "text-gray-400" : "text-orange-400"} />
              #{i + 1}
            </div>
            <div className="font-display text-lg font-bold truncate">{c.name}</div>
            <div className="text-xs text-[var(--text-muted)] mb-2">{c.type} · {c.orders} Bestellungen</div>
            <div className="text-2xl font-display font-bold">{c.total_gross.toFixed(2).replace(".", ",")} €</div>
            <div className="flex items-center gap-1 text-xs text-[var(--text-muted)] mt-1">
              <TrendIcon trend={c.trend} /> Trend: {c.trend === "up" ? "steigend" : c.trend === "down" ? "fallend" : "stabil"}
            </div>
          </div>
        ))}
      </div>

      <div className="mb-3 flex items-center justify-between flex-wrap gap-3">
        <div className="relative max-w-md flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <Input placeholder="Kunde suchen…" value={q} onChange={(e) => setQ(e.target.value)}
                 className="pl-8 rounded-xl h-9" data-testid="input-stats-search" />
        </div>
        <div className="text-sm text-[var(--text-muted)]">Gesamt-Umsatz (alle Kunden): <b className="text-[var(--text)]">{totalGross.toFixed(2).replace(".", ",")} €</b></div>
      </div>

      <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
        <Table className="table-zebra">
          <TableHeader>
            <TableRow>
              <TableHead className="overline">Kunde</TableHead>
              <TableHead className="overline">Typ</TableHead>
              <TableHead className="overline text-right">Bestellungen</TableHead>
              <TableHead className="overline text-right">Umsatz netto</TableHead>
              <TableHead className="overline text-right">Umsatz brutto</TableHead>
              <TableHead className="overline text-right">Letzte 30 T</TableHead>
              <TableHead className="overline text-right">Trend</TableHead>
              <TableHead className="overline">Letzte Bestellung</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((c) => (
              <TableRow key={c.customer_id} data-testid={`stat-row-${c.customer_id}`}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-xs">{c.type}</TableCell>
                <TableCell className="text-right font-mono text-xs">{c.orders}</TableCell>
                <TableCell className="text-right font-mono text-xs">{c.total_net.toFixed(2)}</TableCell>
                <TableCell className="text-right font-mono text-sm font-bold">{c.total_gross.toFixed(2)}</TableCell>
                <TableCell className="text-right font-mono text-xs">{c.gross_last_30d.toFixed(2)}</TableCell>
                <TableCell className="text-right"><span className="inline-flex justify-end"><TrendIcon trend={c.trend} /></span></TableCell>
                <TableCell className="font-mono text-xs">{(c.last_order || "").slice(0, 10) || "—"}</TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-[var(--text-muted)]">Keine Kunden mit Umsatz.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
